/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import _ from "underscore";
import ko from "knockout";
import util from "../../util/util.js";
import * as utmGrid from "../../map/utm-grid.js";

var LocationSearchViewModel = function(params) {
  var self = this;

  params = params || {};

  this.map = params.map;
  this.onResultSelected = params.onResultSelected;
  this.placeholder = params.placeholder || 'Search for site, location or previous survey point';

  if(!this.onResultSelected) {
    console.warn("onResultSelected parameter was not supplied for LocationSearchViewModel");
  }

  self.searchFocused = ko.observable(false).extend({ rateLimit: 300, method: "notifyWhenChangesStop" });

  self.locationResults = ko.observableArray([]);
  self.sharedSiteResults = ko.observableArray([]);
  self.userSurveyPointResults = ko.observableArray([]);
  self.gridCellResults = ko.observableArray([]);

  self.searchResults = ko.pureComputed(function() {
    if(self.searchFocused()) {
      return _.chain([
        self.locationResults(),
        self.sharedSiteResults(),
        self.userSurveyPointResults(),
        self.gridCellResults()
        ])
      .flatten()
      .sortBy(function(result) {
        return result.title.toLowerCase();
      })
      .sortBy(function(result) {
        var offset = result.title.toLowerCase().indexOf(self.searchText().toLowerCase());
        if(offset < 0) offset = 9999;
        return offset;
      })
      .sortBy(params.searchResultSort || function() { return 0; })
      .first(10)
      .value();
    } else {
      return [];
    }
  });

  self.searchText = ko.observable("").extend({ rateLimit: 500 });

  var defaultResultTypes = ['LOCATION', 'SHARED_SITE', 'USER_SURVEY_POINT'];

  function resultTypeEnabled(t) {
    var types = ko.unwrap(params.searchResultTypes) || defaultResultTypes;
    return types.indexOf(t) != -1;
  }

  // Grid cell search
  ko.computed(function() {
    if(!resultTypeEnabled('GRID_CELL')) {
      self.gridCellResults([]);
      return;
    }
    var search = self.searchText().trim().toUpperCase();
    var p = utmGrid.gridCellCentroid(search, 1000);
    if(p) {
      self.gridCellResults([{
        type: 'GRID_CELL',
        title: search,
        subtitle: 'Grid cell',
        data: { centroid: p, cellID: search }
      }]);
    } else {
      self.gridCellResults([]);
    }
  });

  // User survey points search
  if(params.surveyPointsPromise) {
    params.surveyPointsPromise.then(function(surveyPoints) {
      ko.computed(function() {
        if(!resultTypeEnabled('USER_SURVEY_POINT')) {
          self.userSurveyPointResults([]);
          return;
        }

        var search = self.searchText();
        if(search.length > 0) {
          self.userSurveyPointResults(
            _.chain(surveyPoints)
            .filter(function(surveyPoint) {
              var name = ko.unwrap(surveyPoint.name);
              if(name) {
                return name.toLowerCase().indexOf(search.toLowerCase()) >= 0;
              } else {
                return false;
              }
            })
            .map(function(surveyPoint) {
              return {
                type: 'USER_SURVEY_POINT',
                title: ko.unwrap(surveyPoint.name),
                subtitle: 'Previous survey point',
                data: surveyPoint
              };
            })
            .first(10)
            .value());
        }
      });
    });
  }

  // Shared sites search
  if(params.sharedSitesPromise) {
    params.sharedSitesPromise.then(function(sites) {
      ko.computed(function() {
        if(!resultTypeEnabled('SHARED_SITE')) {
          self.sharedSiteResults([]);
          return;
        }

        var search = self.searchText();
        var layers = ko.unwrap(params.sharedSiteLayers);
        if(search.length > 0) {
          self.sharedSiteResults(
            _.chain(sites)
            .filter(function(site) {
              if(layers && layers.indexOf(site.layer) == -1) {
                return false;
              }
              if(site.inactive) {
                return false;
              }
              return site.name.toLowerCase().indexOf(search.toLowerCase()) >= 0;
            })
            .map(function(site) {
              return {
                type: 'SHARED_SITE',
                title: site.name,
                subtitle: 'Shared site',
                data: site
              };
            })
            .value());
        }
      });
    });
  }

  // Location search
  ko.computed(function() {
    if(!resultTypeEnabled('LOCATION')) {
      self.locationResults([]);
      return;
    }

    var search = self.searchText();
    if(search.length > 0) {
      var resultsPromise;

      var map = ko.unwrap(self.map);

      if(map) {
        var bounds = map.getBounds();
        var ne = bounds.getNorthEast();
        var sw = bounds.getSouthWest();
        if(sw.lng() < ne.lng()) {
          resultsPromise = birdata.locationSearch(search, ne.lng(), ne.lat(), sw.lng(), sw.lat());
        } else {
          // bounding box crosses date line, just ignore bounding box then..
          resultsPromise = birdata.locationSearch(search);
        }
      } else {
        resultsPromise = birdata.locationSearch(search);
      }

      resultsPromise.then(function(results) {
        self.locationResults(_.map(results, function(result) {
          var title = result.name;
          if(result.state) {
            title += ", " + result.state;
          }
          return {
            type: 'LOCATION',
            title: title,
            subtitle: result.type,
            data: result
          };
        }));
      });
    }
  });

  self.selectedResult = ko.observable(null);

  function cycleSelection(up) {
    var results = self.searchResults();
    var s = self.selectedResult();
    var index;

    if(s == null || !_.contains(results, s)) {
      index = up ? results.length - 1 : 0;
    } else {
      index = _.indexOf(results, s);
      index = (index + results.length + (up ? -1 : 1)) % results.length;
    }

    self.selectedResult(results[index]);
  }

  function activateSelection() {
    if(self.selectedResult()) {
      self.clickedSuggestion(self.selectedResult());
    }
  }

  self.clickedSuggestion = function(suggestion) {
    self.searchText(suggestion.title);
    if(self.onResultSelected) {
      self.onResultSelected(suggestion);
    }
  }

  self.searchKeyPress = function(d,e) {
    switch(e.keyCode) {
      // case 9: // TAB
      //   return false;

      case 38: // UP
      cycleSelection(true);
      return false;

      case 40: // DOWN
      cycleSelection(false);
      return false;

      case 13: // ENTER
      if(!self.selectedResult() && self.searchResults().length == 1) {
        self.selectedResult(self.searchResults()[0]);
      }
      activateSelection();
      self.searchFocused(false);
      break;

      case 27: // ESC
      self.searchFocused(false);
      self.selectedResult(null);
      break;

      default:
      self.selectedResult(null);
      break;
      //   self.suggestionsVisible(true);
      //   break;
    }
    return true;
  };
};

ko.components.register('location-search', {
  viewModel: LocationSearchViewModel,
  template: require('../../../templates/shared/location-search.html')
});

export default LocationSearchViewModel;
